import { FC, useCallback, useEffect, useState } from 'react';
import { PWABannerView } from './PWABannerView';
import { getDeviceOS } from '../../utils';
import { IOSOutIcon } from '../../icons';
import { Trans, useTranslation } from '../../hooks/useTranslation';
import { BeforeInstallPromptEvent } from './types';

const HIDDEN_LOCALSTORAGE_KEY_NAME = 'ls-pwa-banner-hidden';

export const PWABanner: FC = () => {
    const os = getDeviceOS();
    const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const { t } = useTranslation('ui-kit__pwa-banner');
    const [hidden, setHidden] = useState(!!localStorage.getItem(HIDDEN_LOCALSTORAGE_KEY_NAME));

    const promptToInstall = useCallback(() => {
        if (prompt) {
            return prompt.prompt();
        }
        return Promise.reject(
            new Error('Tried installing before browser sent "beforeinstallprompt" event')
        );
    }, [prompt]);

    useEffect(() => {
        const beforeInstallPromptHandler = (event: BeforeInstallPromptEvent) => {
            event.preventDefault();
            setPrompt(event);
        };

        window.addEventListener('beforeinstallprompt', beforeInstallPromptHandler as never);
        return () =>
            window.removeEventListener('beforeinstallprompt', beforeInstallPromptHandler as never);
    }, []);

    const handleClose = useCallback(() => {
        localStorage.setItem(HIDDEN_LOCALSTORAGE_KEY_NAME, String(Date.now()));
        setHidden(true);
    }, []);

    if (hidden || os === 'other') {
        return null;
    }

    const text =
        os === 'ios' ? (
            <Trans
                ns='ui-kit__pwa-banner'
                i18nKey='ios_message'
                components={[
                    <IOSOutIcon
                        sx={{
                            width: 16,
                            height: 16,
                            color: '#2892f5',
                        }}
                        key='icon'
                    />,
                ]}
            />
        ) : (
            t('android_message')
        );

    return <PWABannerView text={text} onClose={handleClose} onClick={promptToInstall} />;
};
